import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Box,
  CircularProgress,
  Grid2 as Grid,
  Stack,
} from "@mui/material";
import axios from "axios";
import config from "../config.json";
import { useNavigate } from "react-router-dom";
import SyncIcon from "@mui/icons-material/Sync";

const UserReports = () => {
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);

  const navigate = useNavigate();

  const fetchReports = () => {
    setLoadingReports(true);
    axios
      .get(`${config.apiBaseUrl}/user_reports`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      .then((response) => {
        // Sort reports by requested_at in descending order
        const sortedReports = response.data.reports.sort(
          (a, b) => new Date(b.requested_at) - new Date(a.requested_at)
        );
        setReports(sortedReports);
        setLoadingReports(false);
      })
      .catch((error) => {
        setLoadingReports(false);
        console.error("Error fetching reports:", error);
      });
  };

  useEffect(() => {
    // Fetch the reports data from the API
    fetchReports();
  }, []);

  return (
    <Container sx={{ mt: 4 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" gutterBottom>
          <span style={{ fontWeight: "bold" }}>Previous Reports</span> (latest
          first)
        </Typography>
        <Button
          variant="outlined"
          startIcon={<SyncIcon size={20} />}
          onClick={() => fetchReports()}
          disabled={loadingReports}
          sx={{ maxWidth: "150px" }}
        >
          Refresh
        </Button>
      </Box>
      {loadingReports ? (
        <Box
          sx={{
            mt: 4,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "50vh",
          }}
        >
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading reports...
          </Typography>
        </Box>
      ) : (
        <List>
          {reports.map((report) => (
            <ListItem
              key={report._id}
              sx={{
                bgcolor: report.status === "processing" ? "#f0f0f0" : "white",
                padding: "16px",
                borderRadius: "8px",
                marginBottom: "16px",
                border: "1px solid #e0e0e0",
                boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
              }}
            >
              <ListItemText
                primary={`Website: ${report.domain}`}
                secondary={
                  <>
                    <Typography variant="body2">
                      Requested at:{" "}
                      {new Date(report.requested_at + "Z").toLocaleString(
                        "en-US",
                        {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                          hour: "numeric",
                          minute: "numeric",
                          second: "numeric",
                          hour12: true,
                        }
                      )}
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{ display: "block" }}
                    >
                      Requested for: {report?.client_metadata?.client_mail || "----"}
                    </Typography>
                  </>
                }
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mt: { xs: 2, sm: 0 },
                  minWidth: { xs: "100%", sm: "30%" },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mr: { sm: 2 },
                    mb: { xs: 1, sm: 0 },
                  }}
                  color="text.secondary"
                >
                  Status:{" "}
                  <span
                    style={{
                      color:
                        report.status === "complete"
                          ? "green"
                          : report.status === "processing"
                          ? "orange"
                          : "red",
                    }}
                  >
                    {report.status}
                  </span>
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    window.open(`/audit/report/${report.report_id}`, "_blank")
                  }
                  disabled={report.status === "processing" || report.status === "fail"}
                >
                  View Report
                </Button>
              </Box>
            </ListItem>
          ))}
          {reports.length === 0 && (
            <Box
              sx={{
                height: "50vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#1d4ed8",
              }}
            >
              <Typography variant="h4" textAlign="center" sx={{ mt: 4 }}>
                No reports found.
              </Typography>
            </Box>
          )}
        </List>
      )}
    </Container>
  );
};

export default UserReports;
